import WorkOrdersManagementSqauresAPI from 'api/Squares/WorkOrderManagement/sqaures.api';
import { BaseHandler } from 'handlers/base.handler';
import { setApiState } from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import {
  setError,
  setLoading,
  setWorkOrders,
} from 'store/SquaresStore/WorkOrderManagement/WorkOrderManagementState.slice';
import { type WorkOrderCreationPayload } from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import {
  type BulkUpdateWorkOrderPayload,
  type ExportWorkOrderPayload,
  type ImportWorkOrderPayload,
  type WorkOrder,
  type WorkOrderDeletePayload,
} from 'types/Squares/WorkOrderManagement/work.order.management.types';

export default class SquaresWorkOrdersManagementHandler extends BaseHandler {
  private readonly apiWorkOrdersManagement: WorkOrdersManagementSqauresAPI;

  constructor() {
    super();
    this.apiWorkOrdersManagement = new WorkOrdersManagementSqauresAPI();
  }

  async getAllWorkOrders() {
    try {
      this.dispatch(setLoading(true));
      const response = await this.apiWorkOrdersManagement.getAllWorkOrders();

      if (Array.isArray(response)) {
        const workOrdersArray = response.map((workOrder) => ({
          ...workOrder,
          components: Array.isArray(workOrder.components)
            ? workOrder.components
            : Object.values(workOrder.components || {}),
        }));
        this.dispatch(setWorkOrders(workOrdersArray));
      } else if (response && typeof response === 'object') {
        const workOrdersArray = Object.values(response).map(
          (workOrder: any) => ({
            ...workOrder,
            components: Object.values(workOrder.components || {}),
          })
        );
        this.dispatch(setWorkOrders(workOrdersArray));
      } else {
        throw new Error('Invalid data format');
      }
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }
      const temp = 'Failed to fetch work orders: ' + msg;
      this.handleError?.(temp);
      this.dispatch(setError(temp));
      return [];
    } finally {
      this.dispatch(setLoading(false));
    }
  }

  async createWorkOrder(payload: WorkOrderCreationPayload) {
    try {
      this.dispatch(
        setApiState({
          data: null,
          isLoading: true,
          error: null,
        })
      );
      const response = await this.apiWorkOrdersManagement.createWorkOrder(
        payload
      );
      this.dispatch(
        setApiState({
          data: response,
          isLoading: false,
          error: null,
        })
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }
      this.dispatch(
        setApiState({
          data: null,
          isLoading: false,
          error: msg,
        })
      );
      this.handleError?.('Failed to create work order: ' + msg);
      throw error(msg);
    }
  }

  async updateWorkOrder(workOrderId: string, payload: Partial<WorkOrder>) {
    try {
      this.dispatch(
        setApiState({
          data: null,
          isLoading: true,
          error: null,
        })
      );
      const response = await this.apiWorkOrdersManagement.updateWorkOrder(
        workOrderId,
        payload
      );
      this.dispatch(
        setApiState({
          data: response,
          isLoading: false,
          error: null,
        })
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }
      this.dispatch(
        setApiState({
          data: null,
          isLoading: false,
          error: msg,
        })
      );

      this.handleError?.('Failed to update work order: ' + msg);
      throw new Error(msg);
    }
  }

  async deleteWorkOrder(payload: WorkOrderDeletePayload) {
    try {
      this.dispatch(setLoading(true));
      const response = await this.apiWorkOrdersManagement.deleteWorkOrder(
        payload
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }

      this.handleError?.('Failed to create work order: ' + msg);
      return null;
    } finally {
      this.dispatch(setLoading(false));
    }
  }

  async bulkDeleteWorkOrder(payload: string[]) {
    try {
      this.dispatch(setLoading(true));
      const response = await this.apiWorkOrdersManagement.bulkDeleteWorkOrder(
        payload
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }

      this.handleError?.('Failed to create work order: ' + msg);
      return null;
    } finally {
      this.dispatch(setLoading(false));
    }
  }

  async bulkUpdateWorkOrder(payload: BulkUpdateWorkOrderPayload) {
    try {
      this.dispatch(setLoading(true));
      const response = await this.apiWorkOrdersManagement.bulkUpdateWorkOrder(
        payload
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }

      this.handleError?.('Failed to create work order: ' + msg);
      return null;
    } finally {
      this.dispatch(setLoading(false));
    }
  }

  async exportWorkOrder(payload: ExportWorkOrderPayload) {
    try {
      const response = await this.apiWorkOrdersManagement.exportWorkOrder(
        payload
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }

      this.handleError?.('Failed to create work order: ' + msg);
      return null;
    }
  }

  async importWorkOrder(payload: ImportWorkOrderPayload) {
    try {
      const response = await this.apiWorkOrdersManagement.importWorkOrder(
        payload
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message || error.message;
      }
      this.handleError?.('Failed to create work order: ' + msg);
      throw error;
    }
  }
}
